import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useAuth } from '@store/authStore';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    Divider,
} from '@mui/material';
import { useAppTheme } from '@contexts/ThemeContext';
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    DoneAll as DoneAllIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Loop as LoopIcon,
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { Program, User } from '@modules/programManager/types';
import { apiService } from '@modules/programManager/services/apiService';
import { programSchema } from '@modules/programManager/schemas/validationSchemas';
import { formatTableDate } from '@utils/dateFormatter';
// import ProgramDetailsModal from '@modules/programManager/components/ProgramDetailsModal';

const EnhancedProgramList: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();

    // Consistent button styles for View/Edit/Delete icons
    const viewButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.15)',
        color: '#2196F3',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.25)'
        }
    }), [isDarkMode]);

    const editButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.15)',
        color: '#FF9800',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'
        }
    }), [isDarkMode]);

    const deleteButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.15)',
        color: '#F44336',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'
        }
    }), [isDarkMode]);

    // Mark All Complete button styles - Green
    const markCompleteButtonStyles = useMemo(() => ({
        backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)',
        color: '#4CAF50',
        width: '36px',
        height: '36px',
        minWidth: '36px',
        minHeight: '36px',
        padding: 0,
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'
        },
        '&.Mui-disabled': {
            backgroundColor: isDarkMode ? 'rgba(158, 158, 158, 0.1)' : 'rgba(158, 158, 158, 0.1)',
            color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
        }
    }), [isDarkMode]);

    // Predefined programs that cannot be deleted
    const predefinedPrograms = [
        'Nucleus Launch',
        'Pre-Session Planning',
        'Post-Session Follow-up'
    ];

    // Auth store
    const { user } = useAuth();

    // State for programs
    const [programs, setPrograms] = useState<Program[]>([]);
    const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // State for users
    const [users, setUsers] = useState<User[]>([]);

    // State for sessions stats
    const [sessionsStats, setSessionsStats] = useState<any>({});

    // State for search and filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [assignedToFilter, setAssignedToFilter] = useState('');

    // State for program status - removed since no checkbox functionality needed

    // State for dialogs
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // const [detailsModalOpen, setDetailsModalOpen] = useState(false);

    // State for editing
    const [editingProgram, setEditingProgram] = useState<Program | null>(null);
    const [programToDelete, setProgramToDelete] = useState<Program | null>(null);
    // const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);

    // Mark All Complete dialog state
    const [markCompleteDialogOpen, setMarkCompleteDialogOpen] = useState(false);
    const [programToMarkComplete, setProgramToMarkComplete] = useState<Program | null>(null);
    const [taskSummary, setTaskSummary] = useState<{
        pending: number;
        inProgress: number;
        completed: number;
        cancelled: number;
        total: number;
        toBeUpdated: number;
    } | null>(null);
    const [markCompleteLoading, setMarkCompleteLoading] = useState(false);
    const [taskSummaryLoading, setTaskSummaryLoading] = useState(false);

    // Form validation
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(programSchema),
        defaultValues: {
            title: '',
            description: '',
            assignedTo: '',
            dueDate: '',
        }
    });

    // State for additional data
    // const [categories, setCategories] = useState<ChecklistCategory[]>([]);

    // Fetch data on component mount
    useEffect(() => {
        fetchData();
    }, []);

    // Listen for program status updates
    useEffect(() => {
        const handleProgramStatusUpdate = (event: CustomEvent) => {
            console.log('🔄 Program status update event received:', event.detail);
            // Refresh program data when task status changes
            fetchData();
        };

        window.addEventListener('programStatusUpdated', handleProgramStatusUpdate as EventListener);

        return () => {
            window.removeEventListener('programStatusUpdated', handleProgramStatusUpdate as EventListener);
        };
    }, []);

    // Filter programs when search term or filters change
    useEffect(() => {
        filterPrograms();
    }, [searchTerm, statusFilter, assignedToFilter, programs]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchData = async () => {
        try {
            setLoading(true);
            const [programsData, usersData, statsData] = await Promise.all([
                apiService.getSessionPrograms(),
                apiService.getUsers(),
                apiService.getSessionsStats()
            ]);

            console.log('EnhancedProgramList - Fetched programs data:', programsData);
            console.log('EnhancedProgramList - Fetched users data:', usersData);
            console.log('EnhancedProgramList - Programs data type:', typeof programsData);
            console.log('EnhancedProgramList - Programs data length:', programsData.length);

            // Debug: Check for duplicate programs
            const programIds = programsData.map(p => p.id);
            const uniqueIds = Array.from(new Set(programIds));
            console.log('🔍 Program IDs:', programIds);
            console.log('🔍 Unique Program IDs:', uniqueIds);
            console.log('🔍 Duplicate check - Total:', programIds.length, 'Unique:', uniqueIds.length);

            if (programIds.length !== uniqueIds.length) {
                console.warn('⚠️ DUPLICATE PROGRAMS DETECTED!');
                const duplicates = programIds.filter((id, index) => programIds.indexOf(id) !== index);
                console.warn('⚠️ Duplicate IDs:', duplicates);
            }

            programsData.forEach((program, index) => {
                console.log(`Program ${index + 1}:`, {
                    id: program.id,
                    title: program.title,
                    status: program.status,
                    statusType: typeof program.status
                });
            });

            // Remove duplicates if any exist
            const uniquePrograms = programsData.filter((program, index, self) =>
                index === self.findIndex(p => p.id === program.id)
            );

            if (uniquePrograms.length !== programsData.length) {
                console.warn('⚠️ Removed duplicates:', programsData.length - uniquePrograms.length, 'duplicates');
            }

            setPrograms(uniquePrograms);
            setUsers(usersData);
            setSessionsStats(statsData);

            setError(null);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to fetch programs data');
        } finally {
            setLoading(false);
        }
    };

    const filterPrograms = () => {
        let filtered = [...programs];

        if (searchTerm) {
            filtered = filtered.filter(program =>
                program.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                program.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter) {
            filtered = filtered.filter(program => program.status === statusFilter);
        }

        if (assignedToFilter) {
            filtered = filtered.filter(program => program.assignedTo === assignedToFilter);
        }

        // Custom sorting: New programs at top, then predefined programs in order
        const predefinedOrder = [
            'Nucleus Launch',
            'Pre-Session Planning',
            'Logistics & Preparation',
            'Post-Session Follow-up'
        ];

        filtered.sort((a, b) => {
            const aIndex = predefinedOrder.indexOf(a.title);
            const bIndex = predefinedOrder.indexOf(b.title);

            // If both are in predefined order, maintain that order
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }

            // If only one is in predefined order, new program comes first
            if (aIndex !== -1) return 1;
            if (bIndex !== -1) return -1;

            // If neither is in predefined order, sort by creation date (newest first)
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        setFilteredPrograms(filtered);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleRefresh = () => {
        fetchData();
    };


    // const handleProgramTitleClick = (program: Program) => {
    //     setSelectedProgram(program);
    //     setDetailsModalOpen(true);
    // };

    // const handleCloseDetailsModal = () => {
    //     setDetailsModalOpen(false);
    //     setSelectedProgram(null);
    // };

    const handleViewProgram = (program: Program) => {
        navigate(`/management-index/sessions/tasks/${program.id}`);
    };

    const handleEditProgram = (program: Program) => {
        setEditingProgram(program);
        const formatDateForInput = (dateString: string | null) => {
            if (!dateString) return '';
            try {
                // Handle timezone correctly by adding 'Z' if not present
                const dateStr = dateString.includes('Z') ? dateString : dateString + 'Z';
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return '';
                // Convert to local timezone for input
                const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
                return localDate.toISOString().split('T')[0];
            } catch (error) {
                console.error('Error formatting date:', error);
                return '';
            }
        };
        reset({
            title: program.title || '',
            description: program.description || '',
            assignedTo: program.assignedTo || '',
            dueDate: formatDateForInput(program.dueDate || null),
        });
        setEditDialogOpen(true);
    };

    const handleDeleteProgram = (program: Program) => {
        setProgramToDelete(program);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteProgram = async () => {
        if (!programToDelete) return;

        try {
            await apiService.deleteProgram(programToDelete.id);
            setPrograms(programs.filter(p => p.id !== programToDelete.id));
            setDeleteDialogOpen(false);
            setProgramToDelete(null);
        } catch (err) {
            console.error('Error deleting program:', err);
            setError('Failed to delete program');
        }
    };

    // Mark All Complete handlers
    const handleMarkAllComplete = async (program: Program) => {
        console.log('Mark all complete clicked for session:', program.title);
        setProgramToMarkComplete(program);
        setTaskSummaryLoading(true);
        setMarkCompleteDialogOpen(true);

        try {
            const summary = await apiService.getTaskSummary(program.id.toString());
            setTaskSummary(summary);
        } catch (error) {
            console.error('Error fetching task summary:', error);
            setTaskSummary({
                pending: 0,
                inProgress: 0,
                completed: 0,
                cancelled: 0,
                total: 0,
                toBeUpdated: 0
            });
        } finally {
            setTaskSummaryLoading(false);
        }
    };

    const confirmMarkAllComplete = async () => {
        if (!programToMarkComplete) return;

        setMarkCompleteLoading(true);
        try {
            console.log('Marking all tasks complete for session:', programToMarkComplete.id);
            const result = await apiService.markAllTasksComplete(programToMarkComplete.id.toString());
            console.log('Mark all complete result:', result);

            // Close dialog
            setMarkCompleteDialogOpen(false);
            setProgramToMarkComplete(null);
            setTaskSummary(null);

            // Refresh data
            console.log('Refreshing session data...');
            await fetchData();
            console.log('Session data refreshed after mark all complete');
        } catch (error) {
            console.error('Error marking all tasks complete:', error);
            setError('Failed to mark all tasks as complete. Please try again.');
        } finally {
            setMarkCompleteLoading(false);
        }
    };

    const cancelMarkAllComplete = () => {
        setMarkCompleteDialogOpen(false);
        setProgramToMarkComplete(null);
        setTaskSummary(null);
    };

    // Check if user can mark session as complete (Program Manager or Admin only)
    const canMarkComplete = (program: Program) => {
        if (!user) return false;

        const userRole = user.role?.toLowerCase() || '';

        // Admin can mark any session
        if (userRole === 'admin') return true;

        // Program Manager can mark their own sessions
        if (userRole === 'program_manager' || userRole === 'program manager') {
            return program.createdById === user.id;
        }

        return false;
    };

    const handleSaveProgram = handleSubmit(async (data) => {
        if (saving) {
            console.log('⚠️ Form submission already in progress, ignoring duplicate submission');
            return;
        }

        try {
            setSaving(true);
            console.log('🚀 Frontend handleSaveProgram called with:', data);
            console.log('🚀 editingProgram:', editingProgram);

            const programData = {
                ...data
            };

            if (editingProgram) {
                console.log('📝 Updating existing program:', editingProgram.id);
                await apiService.updateProgram(editingProgram.id, programData);
                setPrograms(programs.map(p => p.id === editingProgram.id ? { ...p, ...programData } : p));
            } else {
                console.log('➕ Creating new program with data:', programData);

                // Add creator information
                const programDataWithCreator = {
                    ...programData,
                    name: programData.title, // Add name property
                    startDate: new Date().toISOString(), // Add startDate
                    endDate: programData.dueDate || new Date().toISOString(), // Add endDate
                    status: 'pending' as const, // Add status
                    creatorId: user?.id || null,
                    creatorName: user?.name || 'Unknown',
                    programType: 'sessions' // Add program type for sessions
                };

                console.log('➕ Program data with creator:', programDataWithCreator);
                const newProgram = await apiService.createProgram(programDataWithCreator);
                console.log('✅ New program created:', newProgram);
                setPrograms([...programs, newProgram]);
            }

            setEditDialogOpen(false);
            setEditingProgram(null);
            reset();
        } catch (err) {
            console.error('Error saving program:', err);
            setError('Failed to save program');
        } finally {
            setSaving(false);
        }
    });


    // Helper functions for program status
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return '#4CAF50'; // Green
            case 'in-progress':
            case 'in_progress':
                return '#FF9800'; // Orange
            case 'cancelled':
                return '#f44336'; // Red
            case 'pending':
            default:
                return theme.palette.text.secondary; // Gray
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'completed':
                return 'COMPLETED';
            case 'in-progress':
            case 'in_progress':
                return 'IN-PROGRESS';
            case 'cancelled':
                return 'CANCELLED';
            case 'pending':
            default:
                return 'PENDING';
        }
    };

    // Status change handler - removed since no checkbox functionality needed

    // getStatusColor function removed since not needed

    // getStatusText function removed since not needed

    // const handleBulkDelete = () => {
    //     if (selectedPrograms.length === 0) return;
    //     setProgramToDelete({ id: selectedPrograms[0] } as Program);
    //     setDeleteDialogOpen(true);
    // };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error" action={
                    <Button color="inherit" size="small" onClick={handleRefresh}>
                        Retry
                    </Button>
                }>
                    {error}
                </Alert>
            </Box>
        );
    }

    const calculatedStats = {
        totalPrograms: programs.length,
        activePrograms: programs.filter(p => p.status === 'in-progress' || p.status === 'in_progress').length,
        completedPrograms: programs.filter(p => p.status === 'completed').length,
        inProgressPrograms: programs.filter(p => p.status === 'in-progress' || p.status === 'in_progress').length,
        pendingPrograms: programs.filter(p => p.status === 'pending' || !p.status).length,
        cancelledPrograms: programs.filter(p => p.status === 'cancelled').length,
    };

    return (
        <Box sx={{
            p: { xs: 2, sm: 3 },
            backgroundColor: theme.palette.background.default,
            borderRadius: '8px',
            margin: { xs: '8px', sm: '16px' },
            minHeight: 'calc(100vh - 64px)'
        }}>
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                mb={3}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={{ xs: 2, sm: 0 }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                        color: theme.palette.text.primary,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        fontWeight: { xs: 600, sm: 700 },
                        lineHeight: { xs: 1.2, sm: 1.3 }
                    }}
                >
                    Sessions Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingProgram(null);
                        reset({
                            title: '',
                            description: '',
                            assignedTo: '',
                            dueDate: '',
                        });
                        setEditDialogOpen(true);
                    }}
                    sx={{
                        backgroundColor: '#2196F3',
                        minWidth: { xs: '100%', sm: 'auto' },
                        height: { xs: '48px', sm: '56px' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                            backgroundColor: '#1976D2',
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                            transition: 'transform 0.1s ease',
                        },
                        // Touch-friendly interactions
                        '@media (hover: none) and (pointer: coarse)': {
                            '&:hover': {
                                backgroundColor: '#2196F3',
                            },
                            '&:active': {
                                backgroundColor: '#1976D2',
                                transform: 'scale(0.95)',
                            },
                        },
                    }}
                >
                    Add Session
                </Button>
            </Box>

            {/* Stats Cards */}
            <Box
                display="flex"
                gap={2}
                mb={3}
                flexWrap="wrap"
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    '& > *': {
                        flex: {
                            xs: '1 1 100%',
                            sm: '1 1 calc(50% - 8px)',
                            md: '1 1 calc(33.333% - 12px)',
                            lg: '1 1 calc(25% - 12px)'
                        }
                    }
                }}
            >
                <Card sx={{
                    minWidth: { xs: '100%', sm: 280, md: 300 },
                    maxWidth: '100%',
                    background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0.04) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: isDarkMode
                        ? '1px solid rgba(33, 150, 243, 0.3)'
                        : '1px solid rgba(33, 150, 243, 0.2)',
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                    },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            mb: 1,
                            fontSize: '1rem'
                        }}>
                            Total Sessions
                        </Typography>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', sm: '2rem', md: '2.3rem', lg: '2.5rem' },
                            textShadow: '0 2px 4px rgba(33, 150, 243, 0.3)'
                        }}>
                            {calculatedStats.totalPrograms}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{
                    minWidth: { xs: '100%', sm: 280, md: 300 },
                    maxWidth: '100%',
                    background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 152, 0, 0.04) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: isDarkMode
                        ? '1px solid rgba(255, 152, 0, 0.3)'
                        : '1px solid rgba(255, 152, 0, 0.2)',
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(255, 152, 0, 0.3)',
                    },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            mb: 1,
                            fontSize: '1rem'
                        }}>
                            Active Sessions
                        </Typography>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', sm: '2rem', md: '2.3rem', lg: '2.5rem' },
                            textShadow: '0 2px 4px rgba(255, 152, 0, 0.3)'
                        }}>
                            {calculatedStats.activePrograms}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{
                    minWidth: { xs: '100%', sm: 280, md: 300 },
                    maxWidth: '100%',
                    background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(156, 39, 176, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(156, 39, 176, 0.04) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: isDarkMode
                        ? '1px solid rgba(156, 39, 176, 0.3)'
                        : '1px solid rgba(156, 39, 176, 0.2)',
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(156, 39, 176, 0.3)',
                    },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            mb: 1,
                            fontSize: '1rem'
                        }}>
                            Pending Sessions
                        </Typography>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', sm: '2rem', md: '2.3rem', lg: '2.5rem' },
                            textShadow: '0 2px 4px rgba(156, 39, 176, 0.3)'
                        }}>
                            {calculatedStats.pendingPrograms}
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{
                    minWidth: { xs: '100%', sm: 280, md: 300 },
                    maxWidth: '100%',
                    background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(76, 175, 80, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.04) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: isDarkMode
                        ? '1px solid rgba(76, 175, 80, 0.3)'
                        : '1px solid rgba(76, 175, 80, 0.2)',
                    borderRadius: { xs: 2, sm: 3 },
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
                    },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            mb: 1,
                            fontSize: '1rem'
                        }}>
                            Completed Sessions
                        </Typography>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', sm: '2rem', md: '2.3rem', lg: '2.5rem' },
                            textShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                        }}>
                            {calculatedStats.completedPrograms}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Search and Filters */}
            <Box
                display="flex"
                gap={{ xs: 1.5, sm: 1.5, md: 2 }}
                mb={3}
                alignItems={{ xs: 'stretch', sm: 'center', md: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row', md: 'row' }}
                width="100%"
                flexWrap={{ xs: 'nowrap', sm: 'wrap', md: 'nowrap' }}
                sx={{
                    backgroundColor: isDarkMode
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '8px',
                    padding: { xs: '12px', sm: '16px' },
                    border: isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : `1px solid ${theme.palette.divider}`
                }}
            >
                <TextField
                    label="Search sessions..."
                    placeholder="Enter session name or description..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                            </InputAdornment>
                        ),
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    sx={{
                        flex: { xs: '1 1 100%', sm: '2', md: '2' },
                        width: { xs: '100%', sm: 'auto', md: 'auto' },
                        minWidth: { xs: '100%', sm: '180px', md: '200px' },
                        '& .MuiOutlinedInput-root': {
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.action.hover,
                            '& fieldset': {
                                borderColor: theme.palette.divider,
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.text.primary,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                            '&.Mui-focused': {
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                            },
                            '&.MuiInputLabel-shrink': {
                                color: theme.palette.text.secondary,
                            },
                        },
                    }}
                />
                <FormControl sx={{
                    minWidth: { xs: '100%', sm: '100px', md: '120px' },
                    width: { xs: '100%', sm: 'auto', md: 'auto' },
                    flex: { xs: '1 1 100%', sm: '1', md: '1' }
                }}>
                    <InputLabel
                        shrink={true}
                        sx={{
                            color: theme.palette.text.secondary,
                            '&.Mui-focused': {
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                            },
                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -9px) scale(0.75)',
                                backgroundColor: isDarkMode ? 'transparent' : theme.palette.background.paper,
                                padding: '0 8px',
                                color: theme.palette.text.secondary,
                            },
                        }}
                    >
                        Status
                    </InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        label="Status"
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) {
                                return <em style={{ color: theme.palette.text.secondary }}>Select status</em>;
                            }
                            return selected;
                        }}
                        sx={{
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.action.hover,
                            '& .MuiOutlinedInput-root': {
                                color: theme.palette.text.primary,
                                '& fieldset': {
                                    borderColor: theme.palette.divider,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme.palette.text.primary,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme.palette.text.secondary,
                                '&.Mui-focused': {
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                                },
                                '&.MuiInputLabel-shrink': {
                                    color: theme.palette.text.secondary,
                                }
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                '& legend': {
                                    color: theme.palette.text.secondary,
                                },
                                '&.Mui-focused': {
                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                    '& legend': {
                                        color: theme.palette.text.secondary,
                                    },
                                },
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                '& legend': {
                                    color: theme.palette.text.secondary,
                                },
                            },
                            '& .MuiSelect-icon': {
                                color: theme.palette.text.secondary,
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    backgroundColor: theme.palette.background.default,
                                    '& .MuiMenuItem-root': {
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: theme.palette.action.selected,
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                </FormControl>
                <FormControl sx={{
                    minWidth: { xs: '100%', sm: '100px', md: '120px' },
                    width: { xs: '100%', sm: 'auto', md: 'auto' },
                    flex: { xs: '1 1 100%', sm: '1', md: '1' }
                }}>
                    <InputLabel
                        shrink={true}
                        sx={{
                            color: theme.palette.text.secondary,
                            '&.Mui-focused': {
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.primary.main,
                            },
                            '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -9px) scale(0.75)',
                                backgroundColor: isDarkMode ? 'transparent' : theme.palette.background.paper,
                                padding: '0 8px',
                                color: theme.palette.text.secondary,
                            },
                        }}
                    >
                        Assigned To
                    </InputLabel>
                    <Select
                        value={assignedToFilter}
                        onChange={(e) => setAssignedToFilter(e.target.value)}
                        label="Assigned To"
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) {
                                return <em style={{ color: theme.palette.text.secondary }}>Select assignee</em>;
                            }
                            return selected;
                        }}
                        sx={{
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.action.hover,
                            '& .MuiOutlinedInput-root': {
                                color: theme.palette.text.primary,
                                '& fieldset': {
                                    borderColor: theme.palette.divider,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme.palette.text.primary,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme.palette.text.secondary,
                                '&.Mui-focused': {
                                    color: theme.palette.text.secondary,
                                },
                                '&.MuiInputLabel-shrink': {
                                    color: theme.palette.text.secondary,
                                }
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                '& legend': {
                                    color: theme.palette.text.secondary,
                                },
                                '&.Mui-focused': {
                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                    '& legend': {
                                        color: theme.palette.text.secondary,
                                    },
                                },
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                '& legend': {
                                    color: theme.palette.text.secondary,
                                },
                            },
                            '& .MuiSelect-icon': {
                                color: theme.palette.text.secondary,
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    backgroundColor: theme.palette.background.default,
                                    '& .MuiMenuItem-root': {
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: theme.palette.action.selected,
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="">All Users</MenuItem>
                        {users.map((user) => (
                            <MenuItem key={user.id} value={user.name}>
                                {user.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                        console.log('🔄 Manual refresh button clicked');
                        handleRefresh();
                    }}
                    sx={{
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider,
                        height: { xs: '48px', sm: '56px' },
                        px: { xs: 2, sm: 3 },
                        minWidth: { xs: '100%', sm: 'auto' },
                        width: { xs: '100%', sm: 'auto' },
                        flex: { xs: '1 1 100%', sm: '0 0 auto' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                            borderColor: theme.palette.text.primary,
                            backgroundColor: theme.palette.action.hover,
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                            transition: 'transform 0.1s ease',
                        },
                        // Touch-friendly interactions
                        '@media (hover: none) and (pointer: coarse)': {
                            '&:hover': {
                                borderColor: theme.palette.divider,
                                backgroundColor: 'transparent',
                            },
                            '&:active': {
                                backgroundColor: theme.palette.action.hover,
                                transform: 'scale(0.95)',
                            },
                        },
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Programs Table */}
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <TableContainer
                    component={Paper}
                    sx={{
                        background: isDarkMode
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        overflow: 'auto',
                        maxHeight: {
                            md: '65vh',
                            '@media (min-width: 896px)': '67vh',
                            '@media (min-width: 1024px)': '68vh',
                            '@media (min-width: 1200px)': '70vh',
                            '@media (min-width: 1356px)': '72vh',
                            lg: '70vh'
                        },
                        border: isDarkMode
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : `1px solid ${theme.palette.divider}`,
                        '&::-webkit-scrollbar': {
                            height: {
                                md: '6px',
                                '@media (min-width: 896px)': '6px',
                                '@media (min-width: 1024px)': '7px',
                                '@media (min-width: 1200px)': '8px',
                                '@media (min-width: 1356px)': '8px',
                                lg: '8px'
                            },
                            width: {
                                md: '6px',
                                '@media (min-width: 896px)': '6px',
                                '@media (min-width: 1024px)': '7px',
                                '@media (min-width: 1200px)': '8px',
                                '@media (min-width: 1356px)': '8px',
                                lg: '8px'
                            },
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: isDarkMode
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: isDarkMode
                                ? 'rgba(255, 255, 255, 0.3)'
                                : 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: isDarkMode
                                    ? 'rgba(255, 255, 255, 0.5)'
                                    : 'rgba(0, 0, 0, 0.3)',
                            },
                        },
                    }}
                >
                    <Table sx={{
                        width: '100%',
                        minWidth: {
                            md: '900px',
                            '@media (min-width: 896px)': '900px',
                            '@media (min-width: 1024px)': '950px',
                            '@media (min-width: 1200px)': '1000px',
                            '@media (min-width: 1356px)': '1100px',
                            lg: '1200px'
                        },
                        tableLayout: {
                            md: 'auto',
                            '@media (min-width: 896px)': 'auto',
                            '@media (min-width: 1024px)': 'fixed',
                            '@media (min-width: 1200px)': 'fixed',
                            '@media (min-width: 1356px)': 'fixed',
                            lg: 'fixed'
                        },
                        '& .MuiTableCell-root': {
                            padding: {
                                md: '6px 4px',
                                '@media (min-width: 896px)': '5px 3px',
                                '@media (min-width: 1024px)': '6px 4px',
                                '@media (min-width: 1200px)': '7px 5px',
                                '@media (min-width: 1356px)': '8px 6px',
                                lg: '10px 8px',
                                xl: '12px 10px'
                            },
                            fontSize: {
                                md: '0.7rem',
                                '@media (min-width: 896px)': '0.68rem',
                                '@media (min-width: 1024px)': '0.7rem',
                                '@media (min-width: 1200px)': '0.72rem',
                                '@media (min-width: 1356px)': '0.74rem',
                                lg: '0.8rem',
                                xl: '0.875rem'
                            },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: {
                                md: 1.1,
                                '@media (min-width: 896px)': 1.08,
                                '@media (min-width: 1024px)': 1.1,
                                '@media (min-width: 1200px)': 1.12,
                                '@media (min-width: 1356px)': 1.15,
                                lg: 1.3,
                                xl: 1.4
                            },
                            verticalAlign: 'middle'
                        }
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '120px',
                                        '@media (min-width: 896px)': '120px',
                                        '@media (min-width: 1024px)': '130px',
                                        '@media (min-width: 1200px)': '140px',
                                        '@media (min-width: 1356px)': '150px',
                                        lg: '160px'
                                    }
                                }}>Session Title</TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '140px',
                                        '@media (min-width: 896px)': '140px',
                                        '@media (min-width: 1024px)': '150px',
                                        '@media (min-width: 1200px)': '160px',
                                        '@media (min-width: 1356px)': '170px',
                                        lg: '180px'
                                    }
                                }}>Description</TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '80px',
                                        '@media (min-width: 896px)': '80px',
                                        '@media (min-width: 1024px)': '85px',
                                        '@media (min-width: 1200px)': '90px',
                                        '@media (min-width: 1356px)': '95px',
                                        lg: '100px'
                                    }
                                }}>
                                    Status
                                </TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '100px',
                                        '@media (min-width: 896px)': '100px',
                                        '@media (min-width: 1024px)': '105px',
                                        '@media (min-width: 1200px)': '110px',
                                        '@media (min-width: 1356px)': '115px',
                                        lg: '120px'
                                    }
                                }}>Assigned To</TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '90px',
                                        '@media (min-width: 896px)': '90px',
                                        '@media (min-width: 1024px)': '95px',
                                        '@media (min-width: 1200px)': '100px',
                                        '@media (min-width: 1356px)': '105px',
                                        lg: '110px'
                                    }
                                }}>Created By</TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '110px',
                                        '@media (min-width: 896px)': '110px',
                                        '@media (min-width: 1024px)': '115px',
                                        '@media (min-width: 1200px)': '120px',
                                        '@media (min-width: 1356px)': '125px',
                                        lg: '130px'
                                    }
                                }}>Schedule Date</TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '100px',
                                        '@media (min-width: 896px)': '100px',
                                        '@media (min-width: 1024px)': '105px',
                                        '@media (min-width: 1200px)': '110px',
                                        '@media (min-width: 1356px)': '115px',
                                        lg: '120px'
                                    }
                                }}>Created At</TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '100px',
                                        '@media (min-width: 896px)': '100px',
                                        '@media (min-width: 1024px)': '105px',
                                        '@media (min-width: 1200px)': '110px',
                                        '@media (min-width: 1356px)': '115px',
                                        lg: '120px'
                                    }
                                }}>Updated At</TableCell>
                                <TableCell sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 'bold',
                                    width: {
                                        md: '140px',
                                        '@media (min-width: 896px)': '145px',
                                        '@media (min-width: 1024px)': '150px',
                                        '@media (min-width: 1200px)': '155px',
                                        '@media (min-width: 1356px)': '160px',
                                        lg: '165px'
                                    }
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPrograms.map((program) => (
                                <TableRow key={program.id} hover>
                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: '#64B5F6',
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                                onClick={() => {
                                                    console.log('Program title clicked, navigating to task management for program:', program.id);
                                                    navigate(`/management-index/sessions/tasks/${program.id}`);
                                                }}
                                            >
                                                {program.title}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                        <Typography variant="body2">
                                            {program.description || 'No description'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{
                                        color: theme.palette.text.primary
                                    }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                color: getStatusColor(program.status || 'pending'),
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: program.status === 'completed' ? 'rgba(76, 175, 80, 0.1)' :
                                                    (program.status === 'in-progress' || program.status === 'in_progress') ? 'rgba(255, 152, 0, 0.1)' :
                                                        program.status === 'cancelled' ? 'rgba(244, 67, 54, 0.1)' :
                                                            'rgba(158, 158, 158, 0.1)',
                                                display: 'inline-block',
                                                whiteSpace: 'nowrap'
                                            }}
                                            onClick={() => {
                                                console.log('Status display clicked for program:', program.id);
                                                console.log('Program status:', program.status);
                                                console.log('Program status type:', typeof program.status);
                                                console.log('Status text:', getStatusText(program.status || 'pending'));
                                                console.log('Status color:', getStatusColor(program.status || 'pending'));
                                                console.log('Full program object:', program);
                                            }}
                                        >
                                            {getStatusText(program.status || 'pending')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                        {program.assignedTo || 'Unassigned'}
                                    </TableCell>
                                    <TableCell sx={{
                                        color: theme.palette.text.primary
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {program.creatorRole || 'Unknown'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                        {formatTableDate(program.dueDate)}
                                    </TableCell>
                                    <TableCell sx={{
                                        color: theme.palette.text.primary
                                    }}>
                                        {formatTableDate(program.createdAt)}
                                    </TableCell>
                                    <TableCell sx={{
                                        color: theme.palette.text.primary
                                    }}>
                                        {formatTableDate(program.updatedAt)}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Tasks">
                                                <IconButton
                                                    size="small"
                                                    disableRipple
                                                    onClick={() => handleViewProgram(program)}
                                                    sx={viewButtonStyles}
                                                >
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Program">
                                                <IconButton
                                                    size="small"
                                                    disableRipple
                                                    onClick={() => handleEditProgram(program)}
                                                    sx={editButtonStyles}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={
                                                predefinedPrograms.includes(program.title)
                                                    ? 'Predefined program cannot be deleted'
                                                    : 'Delete Program'
                                            }>
                                                <IconButton
                                                    size="small"
                                                    disableRipple
                                                    onClick={() => {
                                                        if (!predefinedPrograms.includes(program.title)) {
                                                            handleDeleteProgram(program);
                                                        } else {
                                                            // Show alert for predefined programs
                                                            alert('This is a predefined program and cannot be deleted.');
                                                        }
                                                    }}
                                                    sx={deleteButtonStyles}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {program.status !== 'completed' && canMarkComplete(program) && (
                                                <Tooltip title="Mark All Tasks Complete">
                                                    <IconButton
                                                        size="small"
                                                        disableRipple
                                                        onClick={() => handleMarkAllComplete(program)}
                                                        sx={markCompleteButtonStyles}
                                                    >
                                                        <DoneAllIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>

            {/* Programs Cards - Mobile and Tablet */}
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                <Box display="flex" flexDirection="column" gap={2}>
                    {filteredPrograms.map((program) => (
                        <Card key={program.id} sx={{
                            background: isDarkMode
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)',
                            backdropFilter: 'blur(10px)',
                            border: isDarkMode
                                ? '1px solid rgba(255, 255, 255, 0.1)'
                                : `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            p: { xs: 1.5, sm: 2 },
                        }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                <Box flex={1}>
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    color: '#64B5F6',
                                                    textDecoration: 'underline'
                                                }
                                            }}
                                            onClick={() => {
                                                console.log('Mobile program title clicked, navigating to task management for program:', program.id);
                                                navigate(`/management-index/sessions/tasks/${program.id}`);
                                            }}
                                        >
                                            {program.title}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Status:
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            color: getStatusColor(program.status || 'pending'),
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: program.status === 'completed' ? 'rgba(76, 175, 80, 0.1)' :
                                                (program.status === 'in-progress' || program.status === 'in_progress') ? 'rgba(255, 152, 0, 0.1)' :
                                                    program.status === 'cancelled' ? 'rgba(244, 67, 54, 0.1)' :
                                                        'rgba(158, 158, 158, 0.1)',
                                            display: 'inline-block',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onClick={() => {
                                            console.log('Mobile status display clicked for program:', program.id);
                                            console.log('Program status:', program.status);
                                            console.log('Program status type:', typeof program.status);
                                            console.log('Status text:', getStatusText(program.status || 'pending'));
                                            console.log('Status color:', getStatusColor(program.status || 'pending'));
                                            console.log('Full program object:', program);
                                        }}
                                    >
                                        {getStatusText(program.status || 'pending')}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Assigned To:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        {program.assignedTo || 'Unassigned'}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Created By:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                                        {program.creatorRole || 'Unknown'}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Schedule Date:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        {formatTableDate(program.dueDate)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Created At:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        {formatTableDate(program.createdAt)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        Updated At:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        {formatTableDate(program.updatedAt)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box display="flex" justifyContent="flex-end" gap={1}>
                                <Tooltip title="View Tasks">
                                    <IconButton
                                        size="small"
                                        disableRipple
                                        onClick={() => handleViewProgram(program)}
                                        sx={viewButtonStyles}
                                    >
                                        <ViewIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Program">
                                    <IconButton
                                        size="small"
                                        disableRipple
                                        onClick={() => handleEditProgram(program)}
                                        sx={editButtonStyles}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={
                                    predefinedPrograms.includes(program.title)
                                        ? 'Predefined program cannot be deleted'
                                        : 'Delete Program'
                                }>
                                    <IconButton
                                        size="small"
                                        disableRipple
                                        onClick={() => {
                                            if (!predefinedPrograms.includes(program.title)) {
                                                handleDeleteProgram(program);
                                            } else {
                                                // Show alert for predefined programs
                                                alert('This is a predefined program and cannot be deleted.');
                                            }
                                        }}
                                        sx={deleteButtonStyles}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {program.status !== 'completed' && canMarkComplete(program) && (
                                    <Tooltip title="Mark All Tasks Complete">
                                        <IconButton
                                            size="small"
                                            disableRipple
                                            onClick={() => handleMarkAllComplete(program)}
                                            sx={markCompleteButtonStyles}
                                        >
                                            <DoneAllIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Card>
                    ))}
                </Box>

            </Box>

            {/* Edit Program Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { marginTop: '32px' } }}>
                <DialogTitle sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: '1.25rem',
                    fontWeight: 'bold'
                }}>
                    {editingProgram ? '✏️ Edit Session Details' : '➕ Create New Session'}
                </DialogTitle>
                <DialogContent sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    pt: 3
                }}>
                    <Typography variant="body2" sx={{
                        color: theme.palette.text.secondary,
                        mb: 2,
                        fontStyle: 'italic'
                    }}>
                        {editingProgram ? 'Update the session details below:' : 'Fill in the details to create a new session.'}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                        <Box>
                            <Controller
                                name="title"
                                control={control}
                                render={({ field }) => {
                                    const predefinedPrograms = [
                                        'Nucleus Launch',
                                        'Pre-Session Planning',
                                        'Post-Session Follow-up'
                                    ];
                                    const isPredefined = !!(editingProgram && predefinedPrograms.includes(editingProgram.title));

                                    return (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Session Title"
                                            placeholder="Enter session title"
                                            error={!!errors.title}
                                            helperText={errors.title?.message as string}
                                            disabled={isPredefined}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    color: theme.palette.text.primary,
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-focused': {
                                                        color: theme.palette.text.primary,
                                                    },
                                                },
                                            }}
                                        />
                                    );
                                }}
                            />
                        </Box>
                        <Box>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Description"
                                        placeholder="Enter session description"
                                        multiline
                                        rows={3}
                                        error={!!errors.description}
                                        helperText={errors.description?.message as string}
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <Box>
                            <Controller
                                name="assignedTo"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.assignedTo}>
                                        <InputLabel
                                            shrink={true}
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: theme.palette.background.default,
                                                    padding: '0 8px',
                                                },
                                            }}
                                        >
                                            Assigned To
                                        </InputLabel>
                                        <Select
                                            {...field}
                                            displayEmpty
                                            value={field.value || ''}
                                            label="Assigned To"
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return <em style={{ color: theme.palette.text.secondary }}>Select a user</em>;
                                                }
                                                return selected;
                                            }}
                                            sx={{
                                                color: theme.palette.text.primary,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: theme.palette.background.default,
                                                        color: theme.palette.text.primary,
                                                        '& .MuiMenuItem-root': {
                                                            color: theme.palette.text.primary,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover,
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(33, 150, 243, 0.3)',
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                Select a user
                                            </MenuItem>
                                            {users.map((user) => (
                                                <MenuItem key={user.id} value={user.name}>
                                                    {user.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.assignedTo && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                {errors.assignedTo.message as string}
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Box>
                        <Box>
                            <Controller
                                name="dueDate"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Schedule Date"
                                        type="date"
                                        error={!!errors.dueDate}
                                        helperText={errors.dueDate?.message as string}
                                        variant="outlined"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{
                                            '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                                filter: isDarkMode ? 'invert(1)' : 'invert(0)',
                                                cursor: 'pointer',
                                                opacity: 0.7,
                                                '&:hover': {
                                                    opacity: 1
                                                }
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.palette.text.primary,
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDarkMode ? '#ffffff' : theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                                '&.Mui-focused': {
                                                    color: theme.palette.text.primary,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: '16px 24px'
                }}>
                    <Button
                        onClick={() => {
                            setEditDialogOpen(false);
                            setEditingProgram(null);
                        }}
                        variant="outlined"
                        sx={{
                            color: theme.palette.text.primary,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                            borderWidth: 1.5,
                            '&:hover': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                borderWidth: 1.5
                            },
                            '&:focus': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                outline: 'none'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveProgram}
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : null}
                        sx={{
                            color: 'white',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#1565c0'
                            },
                            '&:focus': {
                                outline: 'none'
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                color: 'rgba(0, 0, 0, 0.26)'
                            }
                        }}
                    >
                        {saving ? 'Saving...' : (editingProgram ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} sx={{ '& .MuiDialog-paper': { marginTop: '32px' } }}>
                <DialogTitle sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}>
                    Delete Session
                </DialogTitle>
                <DialogContent sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    pt: 3
                }}>
                    <Typography>
                        Are you sure you want to delete "{programToDelete?.title}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        variant="outlined"
                        sx={{
                            color: theme.palette.text.primary,
                            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
                            borderWidth: 1.5,
                            '&:hover': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                                borderWidth: 1.5
                            },
                            '&:focus': {
                                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                outline: 'none'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={confirmDeleteProgram} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mark All Complete Confirmation Dialog */}
            <Dialog
                open={markCompleteDialogOpen}
                onClose={cancelMarkAllComplete}
                maxWidth="xs"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '12px',
                        marginTop: '32px'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <DoneAllIcon sx={{ color: '#4CAF50' }} />
                    Mark All Complete
                </DialogTitle>
                <DialogContent sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    pt: 3
                }}>
                    <Typography sx={{ color: theme.palette.text.primary, mb: 2 }}>
                        This will mark all pending and in-progress tasks as completed for "{programToMarkComplete?.title}".
                    </Typography>

                    {taskSummaryLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : taskSummary && taskSummary.toBeUpdated > 0 && (
                        <Typography sx={{ fontWeight: 'bold', color: '#4CAF50', mb: 2 }}>
                            {taskSummary.toBeUpdated} task(s) will be marked complete.
                        </Typography>
                    )}

                    <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                        This action cannot be undone.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    p: 2,
                    gap: 1
                }}>
                    <Button
                        onClick={cancelMarkAllComplete}
                        variant="outlined"
                        disabled={markCompleteLoading}
                        sx={{
                            color: theme.palette.text.secondary,
                            borderColor: theme.palette.divider,
                            '&:hover': {
                                borderColor: theme.palette.text.primary,
                                backgroundColor: theme.palette.action.hover,
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmMarkAllComplete}
                        variant="contained"
                        disabled={markCompleteLoading || taskSummaryLoading || (taskSummary?.toBeUpdated === 0)}
                        startIcon={markCompleteLoading ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
                        sx={{
                            backgroundColor: '#4CAF50',
                            '&:hover': {
                                backgroundColor: '#388E3C'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.5)',
                                color: 'white'
                            }
                        }}
                    >
                        {markCompleteLoading ? 'Processing...' : 'Mark Complete'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default EnhancedProgramList;

